import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { computeDmRegion } from "@ano/shared/utils";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { generateId, normalizeMemberIds } from "./helpers";
// region-guard removed for DM mutators — single-region US cutover
// (2026-05-18). All DMs land in US PG; no cross-region routing.
// region-guard still used by actions-bootstrap.ts for workspace creation.
// See .claude/plans/look-at-this-issue-imperative-nygaard.md Phase 1f.

// Action mutators that ensure a channel of a specific shape exists between
// the actor and one or more counterparties. Each is idempotent — repeated
// calls reuse the existing row and unhide the caller's membership when
// applicable, instead of producing duplicates.
export const dmActionsMutators = {
  getOrCreateDirectDM: defineMutator(
    z.object({
      workspaceId: z.string().optional(),
      actorUserId: z.string(),
      otherUserId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.actorUserId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const { actorUserId, otherUserId } = args;
      // Self-DM: actor === other. The channel has 1 active member
      // (the actor) and acts as a "Notes" surface — issue #1450.
      const isSelfDm = actorUserId === otherUserId;

      // Find existing DM between these users (workspace-independent).
      // For self-DM the channel only has one member; the lookup below
      // collapses both whereExists into a single membership check.
      const existingCandidates = isSelfDm
        ? await tx.run(
            zql.channels
              .where("type", "dm")
              .whereExists("members", (q) => q.where("user_id", actorUserId))
              .related("members"),
          )
        : await tx.run(
            zql.channels
              .where("type", "dm")
              .whereExists("members", (q) => q.where("user_id", actorUserId))
              .whereExists("members", (q) => q.where("user_id", otherUserId))
              .related("members"),
          );
      const expectedMembers = isSelfDm
        ? [actorUserId]
        : normalizeMemberIds([actorUserId, otherUserId]);

      const matching = existingCandidates.filter((channel) => {
        if (isSelfDm) {
          // A self-DM is a channel where (a) no row has ever been
          // recorded for any user other than the actor — tombstones
          // count, otherwise a tombstoned 1:1 would false-match — and
          // (b) the actor has at least one active membership row, so
          // the channel is still reachable from the actor's side.
          //
          // The publication filters `channel_members` to
          // `removed_at IS NULL`, so client-side we can't see
          // tombstones at all — `channel.members` is already the
          // active set. That makes (a) impossible to enforce purely
          // from the data: a regular 1:1 DM whose other party was
          // soft-deleted has the same client-visible shape as a
          // self-DM (one active row, the actor).
          //
          // Use channel.name as the marker instead. Self-DMs are
          // created with name="Notes"; migrations 133 + 134 backfilled
          // pre-existing orphans to the same name. A stale 1:1 DM
          // named "Alice" stays its own thing and won't false-match
          // here even if Alice's membership row gets tombstoned.
          if (channel.name !== "Notes") return false;
          if (!channel.members.every((m) => m.user_id === actorUserId)) {
            return false;
          }
          return channel.members.some((m) => m.removed_at == null);
        }
        const ids = normalizeMemberIds(
          channel.members.map((member) => member.user_id),
        );
        if (ids.length !== expectedMembers.length) return false;
        return ids.every((id, i) => id === expectedMembers[i]);
      });

      if (matching.length > 0) {
        // DM exists — unhide the caller's membership if hidden.
        // "Get or create DM" means the user wants to talk to this person,
        // so re-opening a hidden DM should make it visible again.
        if (tx.location === "server") {
          const callerMembership = await tx.run(
            zql.channel_members
              .where("channel_id", matching[0].id)
              .where("user_id", actorUserId)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (callerMembership?.hidden) {
            await tx.mutate.channel_members.update({
              id: callerMembership.id,
              hidden: false,
            });
          }
        }
        return;
      }

      const otherUser = isSelfDm
        ? null
        : await tx.run(zql.users.where("id", otherUserId).one());
      const actorUser = await tx.run(zql.users.where("id", actorUserId).one());
      const now = Date.now();
      const channelId = generateId();

      // Single-region beta (2026-05-18): all DMs land in US PG. The
      // region column is set to 'us' uniformly; lower-userId-wins is
      // dormant (function preserved for forward-compat). Region-guard
      // removed because there is no multi-region routing to defend.
      const region = computeDmRegion(
        { id: actorUserId, home_region: actorUser?.home_region },
        otherUser
          ? { id: otherUser.id, home_region: otherUser.home_region }
          : null,
      );

      await tx.mutate.channels.insert({
        id: channelId,
        name: isSelfDm ? "Notes" : (otherUser?.display_name ?? "DM"),
        type: "dm",
        is_private: false,
        created_by: actorUserId,
        created_at: now,
        is_archived: false,
        posting_restriction: "everyone",
        region,
      });

      await tx.mutate.channel_members.insert({
        id: generateId(),
        channel_id: channelId,
        user_id: actorUserId,
        is_channel_manager: false,
        joined_at: now,
        muted: false,
        notify: "all",
        hidden: false,
      });

      if (!isSelfDm) {
        await tx.mutate.channel_members.insert({
          id: generateId(),
          channel_id: channelId,
          user_id: otherUserId,
          is_channel_manager: false,
          joined_at: now,
          muted: false,
          notify: "all",
          hidden: false,
        });
      }

      return;
    },
  ),

  getOrCreateGroupDM: defineMutator(
    z.object({
      workspaceId: z.string().optional(),
      actorUserId: z.string(),
      memberUserIds: z.array(z.string()).min(2),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.actorUserId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const normalizedMembers = normalizeMemberIds([
        args.actorUserId,
        ...args.memberUserIds,
      ]);

      if (normalizedMembers.length < 3) {
        throw new Error("Group direct message requires at least 3 members");
      }

      // Find existing group DM with the same members (workspace-independent)
      const existingGroupCandidates = await tx.run(
        zql.channels.where("type", "group_dm").related("members"),
      );

      const matching = existingGroupCandidates.filter((channel) => {
        const ids = normalizeMemberIds(
          channel.members.map((member) => member.user_id),
        );
        if (ids.length !== normalizedMembers.length) return false;
        return ids.every((id, index) => id === normalizedMembers[index]);
      });

      if (matching.length > 0) {
        // Group DM exists — unhide the caller's membership if hidden.
        if (tx.location === "server") {
          const callerMembership = await tx.run(
            zql.channel_members
              .where("channel_id", matching[0].id)
              .where("user_id", args.actorUserId)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (callerMembership?.hidden) {
            await tx.mutate.channel_members.update({
              id: callerMembership.id,
              hidden: false,
            });
          }
        }
        return;
      }

      const users = await tx.run(
        zql.users.where("id", "IN", normalizedMembers),
      );
      const nameById = new Map(
        users.map((user) => [user.id, user.display_name]),
      );
      const channelName = normalizedMembers
        .filter((id) => id !== args.actorUserId)
        .map((id) => nameById.get(id) ?? id)
        .join(", ");

      // Single-region beta (2026-05-18): all group DMs land in US PG.
      // lower-userId-wins dormant; region always 'us'.
      const region = "us" as const;

      const now = Date.now();
      const channelId = generateId();
      await tx.mutate.channels.insert({
        id: channelId,
        name: channelName || "Group DM",
        type: "group_dm",
        is_private: false,
        created_by: args.actorUserId,
        created_at: now,
        is_archived: false,
        posting_restriction: "everyone",
        region,
      });

      for (const userId of normalizedMembers) {
        await tx.mutate.channel_members.insert({
          id: generateId(),
          channel_id: channelId,
          user_id: userId,
          is_channel_manager: false,
          joined_at: now,
          muted: false,
          notify: "all",
          hidden: false,
        });
      }

      return;
    },
  ),

  ensureWorkspaceSystemLogChannel: defineMutator(
    z.object({
      workspaceId: z.string(),
      actorUserId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.actorUserId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const actorMembership = await tx.run(
        zql.workspace_members
          .where("workspace_id", args.workspaceId)
          .where("user_id", args.actorUserId)
          .where("removed_at", "IS", null)
          .one(),
      );
      if (!actorMembership) {
        // Silent no-op for kicked / not-yet-bootstrapped actors.
        //
        // This mutator is fired by recurring background code
        // (apps/desktop/src/lib/system-log.ts) every time the desktop
        // app picks a workspace from `queries.workspaces.byUser()`.
        // After a kick, there's a race window where the client's local
        // Zero cache still shows the old (now-removed) workspace
        // membership, fires this mutator, and the server-side
        // membership check rejects.
        //
        // Throwing here propagates back to the client as a Zero
        // poke-processing error, which `PokeHandler` treats as a
        // connection-fatal event — the client closes the WebSocket
        // and enters a reconnect loop. Once the loop is running, every
        // batched mutator (`user_presence.setOnline`, `markChannelRead`,
        // `markChannelNotificationsRead`, …) appears to "fail" in the
        // console even though they're collateral damage of the
        // failed batch. The reconnect re-fires the same mutators,
        // server rejects again, infinite loop. Symptom: kicked user's
        // local Zero state never picks up the workspace_members
        // update so the `MembershipLost` dialog takes minutes to
        // appear (or never appears until the app is restarted).
        //
        // Silently returning here matches the SAFE-action semantic:
        // there's nothing to bootstrap on behalf of a non-member;
        // doing nothing is the correct outcome. The `ctx.sub` check
        // above still gates against caller-identity spoofing — only
        // the membership-lifecycle check is relaxed.
        return;
      }

      const systemBotId = "system-bot";
      const existingSystemBot = await tx.run(
        zql.users.where("id", systemBotId).one(),
      );
      if (!existingSystemBot) {
        // Phase 1.5.6 — `is_online` was dropped from the Zero `users`
        // schema. Identity-only fields go on `users`; the system bot's
        // presence row (with is_online: true) is created below on
        // `user_presence`.
        await tx.mutate.users.upsert({
          id: systemBotId,
          email: "system@ano.dev",
          display_name: "System",
          created_at: Date.now(),
          is_deactivated: false,
          is_guest: false,
          is_coworker: false,
          agent_status_opt_in: false,
          // WS-B1: system bot doesn't have a meaningful region; default 'us'.
          home_region: "us",
          profile_updated_at: Date.now(),
        });
        // `insert` is no-op-on-conflict (per Zero's TableCRUD type
        // defs); the surrounding `if (!existingSystemBot)` guard
        // already prevents a clobber, but using insert here is
        // belt-and-suspenders against any future race.
        await tx.mutate.user_presence.insert({
          user_id: systemBotId,
          is_online: true,
          terminal_share: false,
          typing_in: null,
          typing_at: null,
          last_active_at: null,
          status: null,
          status_emoji: null,
          status_expiry: null,
          terminal_process: null,
          terminal_cwd: null,
          terminal_active_at: null,
        });
      }

      // Channel creation + member fanout intentionally removed: the
      // auto-created #logs channel was unwanted UX (fanned out with
      // notify="all" + badged via the unread-counts path that doesn't
      // filter kind='system'). Existing `postSystemLog` call sites
      // become silent no-ops — `system-log.ts:getOrCreateLogsChannel`
      // finds no row and returns null, so `postSystemLog` early-returns.
      // This mutator is preserved as the system-bot upsert entry point —
      // `createThread` and `server/routes/channel-routes.ts` add-to-channel
      // both INSERT messages with `user_id: 'system-bot'` and rely on the
      // row existing.
    },
  ),
};
